import {
  Component,
  ViewEncapsulation,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { getCardOptions } from 'src/old-pages/booking/confirm/confirm-card-options';
import { StripeProvider } from 'src/providers/stripe/stripe-provider';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { SharedLinkService } from 'src/shared/services/shared-link.service';
import { ThemeService } from 'src/shared/services/theme.service';

import { SharedLinkResponse } from 'src/models/shared-link.model';

import { TidyStorage } from 'src/shared/providers/tidy-storage';

@Component({
  templateUrl: 'add-payment-method.html',
  styleUrls: ['add-payment-method.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class SharedLinkAddPaymentMethodPage implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('cardInfo', { static: false }) cardInfo: ElementRef;
  cardEl: any;
  cardHandler = this.onChange.bind(this);
  errorMessage = '';
  uuid: string;
  sharedLink: SharedLinkResponse;
  customerName = '';
  hasValidCaptcha: string | null = null;
  submitting = false;
  submitted = false;
  success = false;
  cardLast4 = '';
  isRightSideContent = false;
  form: UntypedFormGroup;

  boundSubmitCard = this.submitCard.bind(this);

  constructor(
    private cd: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private stripeProvider: StripeProvider,
    private navCtrl: CustomNavController,
    private sharedLinkService: SharedLinkService,
    private storage: TidyStorage,
    private themeService: ThemeService
  ) {
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
    });
  }

  async ngOnInit(): Promise<void> {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    const dialogParams = await this.storage.retrieve('dialog-params');
    this.uuid = this.navCtrl.getParam('uuid') || dialogParams?.uuid;
    this.sharedLink = this.navCtrl.getParam('sharedLink') || dialogParams?.sharedLink;
    const customer = this.sharedLink?.shared_link_data?.customer;
    if (customer) {
      this.customerName = customer.first_name || '';
      this.form.patchValue({
        firstName: customer.first_name || '',
        lastName: customer.last_name || '',
        email: customer.email || '',
      });
    }
  }

  ngAfterViewInit(): void {
    this.initiateCardElement();
  }

  ngOnDestroy(): void {
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      this.cardEl.destroy();
      this.cardEl = null;
    }
  }

  initiateCardElement(): void {
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      this.cardEl.destroy();
      this.cardEl = null;
    }
    setTimeout(() => {
      const isLightMode = !this.themeService.isDarkMode();
      this.cardEl = this.stripeProvider
        .getElements()
        .create('card', getCardOptions(isLightMode));
      this.cardEl.mount(this.cardInfo.nativeElement);
      this.cardEl.addEventListener('change', this.cardHandler);
    }, 1000);
  }

  onChange(err: any): void {
    this.errorMessage = err.error ? err.error.message : '';
    this.cd.detectChanges();
  }

  captchaEvents(captchaResult: string | null): void {
    this.hasValidCaptcha = captchaResult;
  }

  async submitCard(): Promise<void> {
    this.errorMessage = '';
    this.submitted = true;
    if (this.form.invalid) {
      return;
    }
    if (!this.hasValidCaptcha) {
      this.errorMessage = 'Please check that you are not a robot.';
      return;
    }
    this.submitting = true;
    const { token, error } = await this.stripeProvider
      .getInstance()
      .createToken(this.cardEl);

    if (error) {
      this.errorMessage = error.message;
      this.submitting = false;
      return;
    }

    try {
      await this.sharedLinkService.executeAction(
        this.uuid,
        'add_payment_method',
        {
          stripe_card_token: token.id,
          first_name: this.form.value.firstName,
          last_name: this.form.value.lastName,
        }
      );
      this.cardLast4 = token.card?.last4 || '';
      this.success = true;
    } catch (err) {
      this.errorMessage = err?.message || err?.error?.message || 'Something went wrong. Please try again.';
    } finally {
      this.submitting = false;
    }
  }
}
